// @flow
import React, { PureComponent } from "react";
import { connect, Dispatch } from "react-redux";
import SearchForm from "components/SearchForm";
import Status from "components/Status";
import {
    getSearchFormAction,
    setSearchFormChangesAction,
    searchAbortAction,
    searchResetAction,
    searchFormValidationAction,
} from "data/actions";
import {
    type SearchFormResultDTO,
    type SearchItemDTO,
    type SearchFilterDTO,
    SearchItemType,
    FormLookupValue,
} from "data/types";
import Constants, {
    isFeatureOn,
    Feature,
    type ConstantsEnum,
} from "data/constants";
import { produce } from "immer";
import isEqual from "lodash/isEqual";
import debounce from "lodash/debounce";
import * as s from "data/reducers/selectors";
import FormBarFactory from "components/FormBar/Factory";
import { FormBarView } from "data/types";

type Props = {
    /** search form id to render */
    id: number,
    /** optional extra filters to set on searchRequest */
    extraFilters?: Array<SearchFilterDTO>,
    /** current itemUri */
    itemUri: string,
    /** label to show on Search button */
    searchLabel: string,
    /** callback when user clicks the Search button
     * @param {SearchItemDTO} searchItem searchItem containing query
     */
    onSearch: (searchItem: SearchItemDTO) => void,
    /** callback when user clicks the Cancel button */
    onCancel: () => void,
    /** callback when user clicks Back */
    onBack: () => void,
    /** Redux dispatch */
    dispatch: Dispatch,
    /** the search form itself */
    form: ?SearchFormResultDTO,
    /** search form fetch status */
    status: ConstantsEnum,
    /** whether to enable the search button */
    searchEnabled?: boolean,
    /** whether to show search operators */
    showSearchOperators?: boolean,
};

type State = {
    changed: Object,
    form: ?SearchFormResultDTO,
};

const views = [FormBarView.Search, FormBarView.SearchScope];

export class SearchFormContainer extends PureComponent<Props, State> {
    state = {
        changed: {},
        form: undefined,
    };

    static defaultProps = {
        searchEnabled: true,
        showSearchOperators: false,
    };

    // Debounce settings
    static DEBOUNCE_OPTIONS = {
        leading: false,
        trailing: true,
    };
    static DEBOUNCE_TIME = 150;

    constructor(props) {
        super(props);
        this.form = React.createRef();
    }

    componentDidMount = () => this._fetch(this.props.id);

    componentDidUpdate(prevProps: Props) {
        /* istanbul ignore else */
        if (
            prevProps.itemUri !== this.props.itemUri ||
            prevProps.id !== this.props.id
        ) {
            this._fetch(this.props.id);
        }
        /* istanbul ignore else */
        if (
            prevProps.form !== this.props.form &&
            this.props.form != null &&
            this.props.form.attributes != null
        ) {
            this.resetForm(prevProps, this.props.changes);
        }
    }

    resetForm = (prevProps, changes = {}) => {
        const { changed, form, formsy } = produce(
            { form: this.props.form },
            (draft) => {
                draft.changed = {};
                draft.formsy = {};
                draft.form.attributes = draft.form.attributes.map((attr) => {
                    let { value: val, op } = attr;
                    // prefill free text search with value from store...
                    if (
                        (prevProps.form == null ||
                            prevProps.form.name === this.props.form.name ||
                            attr.name === "$SEARCHTEXT$") &&
                        changes[attr.name]
                    ) {
                        val = changes[attr.name].val;
                        op = changes[attr.name].op;
                    }
                    attr.op = op;
                    attr.value = val;
                    draft.changed[attr.name] = {
                        val,
                        op,
                    };
                    if (Array.isArray(val)) {
                        val.forEach(
                            (v, i) =>
                                (draft.formsy[`sf.${attr.name}[${i}]`] = v)
                        );
                    } else {
                        draft.formsy[`sf.${attr.name}`] = val;
                    }
                    return attr;
                });
                if (
                    this.props.extraFilters != null &&
                    this.props.extraFilters.length > 0
                ) {
                    let customId = -50;
                    this.props.extraFilters.forEach((filter) => {
                        // add to form definition
                        draft.form.attributes.splice(0, 0, {
                            hidden: true,
                            id: customId--,
                            header: filter.name,
                            name: filter.name,
                            value: filter.value,
                            valueType: filter.valueType,
                            op: filter.op,
                            nodeType: filter.nodeType,
                        });
                        // add values
                        draft.changed[filter.name] = {
                            val: filter.value,
                            op: filter.op,
                        };
                    });
                }
                draft.form.name = this.props.name || draft.form.name;
            }
        );
        this.setState(
            {
                changed,
                form,
            },
            () => {
                this.form.current?.updateInputsWithValue(formsy);
                this.props.dispatch(setSearchFormChangesAction(changed));
                if (isFeatureOn(Feature.searchAsYouType)) this._onSearch();
            }
        );
    };

    _fetch = (id: number): void => {
        this.props.dispatch(
            getSearchFormAction({
                id,
                model: this.state.changed,
            })
        );
    };

    _hasChanged = () =>
        (this.state.form?.itemUri != null &&
            this.state.form?.itemUri !== this.props.form?.itemUri) ||
        (this.state.form?.scope != null &&
            this.state.form?.scope !== this.props.form?.scope) ||
        this.state?.form?.attributes?.some((attr, ind) => {
            // Operator changed?
            if (this.props.form?.attributes?.[ind]?.op !== attr.op) return true;
            // Value changed?
            const defaultValue = this.props.form?.attributes?.[ind]?.value;
            const currentValue = attr.value;
            if (Array.isArray(currentValue) && !defaultValue) {
                return currentValue.some((v) => !!v);
            }
            if (defaultValue == null) return !!currentValue;
            return !isEqual(defaultValue, currentValue);
        });

    _onValidated = debounce(
        (isValid: boolean) => {
            if (!isFeatureOn(Feature.searchAsYouType) || !this.props.isOnline)
                return;
            this.props.dispatch(searchFormValidationAction(isValid));
            if (isValid) {
                this._onSearch();
            } else {
                this.props.dispatch(searchResetAction());
            }
        },
        SearchFormContainer.DEBOUNCE_TIME,
        SearchFormContainer.DEBOUNCE_OPTIONS
    );

    _onChange = (name: string, value: any) => {
        this.props.dispatch(searchAbortAction());
        const splittedName = name.split(".");
        if (
            value == null &&
            !Object.prototype.hasOwnProperty.call(
                this.state.changed,
                splittedName[1]
            )
        ) {
            return;
        }
        const oldValue = this.state.changed[splittedName[1]];
        if (
            oldValue != null &&
            value != null &&
            (oldValue.val !== value.val || oldValue.op !== value.op)
        ) {
            const { changed, form } = produce(this.state, (draft) => {
                const idx = this.state.form.attributes.findIndex(
                    (a) => a.name === splittedName[1]
                );
                // set operator
                draft.form.attributes[idx].op = value.op;
                // set value
                draft.form.attributes[idx].value = value.val;
                draft.changed[splittedName[1]] = value;
            });
            // update local state
            this.setState(
                {
                    changed,
                    form,
                },
                () => {
                    this.props.dispatch(setSearchFormChangesAction(changed));
                }
            );
        }
    };

    handleClearForm = () => {
        this.resetForm(this.props);
    };

    _toFilterValue = (value: string | FormLookupValue) =>
        Array.isArray(value)
            ? value.map((v) => this._toFilterValue(v))
            : value && (value.T || value);

    // send our search model with searchOperators
    _onSearch = () => {
        this.props.dispatch(searchAbortAction());
        const { changed, form } = this.state;

        // convert model into filters
        const filters = [];
        let searchText = null;
        form.attributes.forEach((attr) => {
            const changedField = changed[attr.name];
            /* istanbul ignore else */
            if (changedField != null) {
                filters.push({
                    name: attr.name,
                    op: changedField.op,
                    value: this._toFilterValue(changedField.val),
                    valueType: attr.valueType,
                    nodeType: attr.nodeType,
                    keywordField: attr.keywordField,
                });
                // keep special searchText for highlighting
                if (attr.name === "$SEARCHTEXT$")
                    searchText = this._toFilterValue(changedField.val);
            }
        });

        // build searchItem
        const searchItem: SearchItemDTO = {
            id: this.props.id,
            name: this.props.form.name,
            itemUri: this.props.itemUri ?? form.itemUri,
            scope: form.scope,
            searchType: SearchItemType.Form,
            searchText,
            filters,
            sorts: this.props.form.sorts,
        };

        /* istanbul ignore else */
        if (typeof this.props.onSearch === "function")
            this.props.onSearch(searchItem);
    };

    _handleSearchScopeChange = (scope, itemUri) =>
        this.setState((state) => ({
            form: produce(state.form, (draft) => {
                draft.scope = scope;
                draft.itemUri = itemUri;
            }),
        }));

    render = () => (
        <>
            <FormBarFactory
                views={views}
                itemUri={this.state.form?.itemUri}
                searchScope={this.state.form?.scope}
                scopeItemUri={this.state.form?.itemUri}
                onSearchScopeChange={this._handleSearchScopeChange}
                searchForm={this.props.form}
                onCancel={this.props.onCancel}
                onBack={this.props.onBack}
                onClear={this._hasChanged() && this.handleClearForm}
                status={this.props.searchStatus}
            />
            {this.props.status !== Constants.OK && (
                <Status status={this.props.status} />
            )}
            {this.props.status === Constants.OK && this.state.form == null && (
                <Status status={Constants.ERROR} />
            )}
            {this.props.status === Constants.OK && this.state.form != null && (
                <SearchForm
                    key={`sf-${this.props.id}`}
                    ref={this.form}
                    form={this.state.form}
                    searchLabel={this.props.searchLabel}
                    searchEnabled={
                        this.props.searchEnabled && this.props.isOnline
                    }
                    showSearchOperators={this.props.showSearchOperators}
                    onCancel={this.props.onBack || this.props.onCancel}
                    onSearch={this._onSearch}
                    onChange={this._onChange}
                    onValidated={this._onValidated}
                />
            )}
        </>
    );
}

const mapStateToProps = /* istanbul ignore next */ (state, ownProps) => ({
    status: s.searchFormStatusSelector(state),
    searchStatus: s.searchStatusSelector(state),
    form: s.searchFormResultSelector(state),
    showSearchOperators: s.settingValueSelector("showSearchOperators", state),
    changes: s.searchFormChangesSelector(state),
    isOnline: s.isOnlineSelector(state),
    ...ownProps,
});

export default connect(mapStateToProps)(SearchFormContainer);
